import React, { useState } from 'react';
import AuthLayout from '../common/AuthLayout';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import { authService } from '../../../services/api';

const MultiStepSignup = ({ onNavigate }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [formData, setFormData] = useState({
    // Step 1
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2
    name: '',
    gender: '',
    age: '',
    country: '',
    city: '',
    society: ''
  });

  const handleNext = (step1Data) => {
    // Check if user wants to navigate to login instead
    if (step1Data.navigateTo === 'login') {
      onNavigate('login');
      return;
    }
    
    setFormData(prev => ({ ...prev, ...step1Data }));
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleBackToHome = () => {
    onNavigate('main');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepOne
            formData={formData}
            onNext={handleNext}
            onBackToHome={handleBackToHome}
            onNavigate={onNavigate}
          />
        );
      case 2:
        return (
          <StepTwo
            formData={formData}
            onBack={handleBack}
            loading={loading}
            onComplete={(step2Data) => {
              const finalData = { ...formData, ...step2Data };
              handleComplete(finalData);
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleComplete = async (finalData) => {
    try {
      setLoading(true);
      setRegistrationError('');
      
      const { confirmPassword, name, ...dataToSend } = finalData;
      const cleanData = Object.fromEntries(
        Object.entries(dataToSend).filter(([_, value]) => value !== '')
      );

      const trimmedName = (name || '').trim();
      if (trimmedName) {
        const parts = trimmedName.split(/\s+/);
        cleanData.first_name = parts[0];
        cleanData.last_name = parts.slice(1).join(' ');
      }

      if (!cleanData.country) {
        cleanData.country = 'Pakistan';
      }

      await authService.register(cleanData);
      // Registration successful, navigate to email verification page
      onNavigate('verify-email', { email: finalData.email });
    } catch (error) {
      // Handle registration errors
      console.error('Registration failed:', error);
      
      // Extract error message
      let errorMessage = error?.message || 'Registration failed. Please try again.';
      
      // If it's a validation error with a specific message, use it
      if (error?.type === 'validation_error' && error?.message) {
        errorMessage = error.message;
      } else if (error?.errors) {
        // Try to extract message from errors object
        if (error.errors.email) {
          const emailError = Array.isArray(error.errors.email) 
            ? error.errors.email[0] 
            : error.errors.email;
          errorMessage = emailError;
        } else if (error.errors.message) {
          errorMessage = error.errors.message;
        } else if (error.errors.detail) {
          errorMessage = error.errors.detail;
        }
      }
      
      setRegistrationError(errorMessage);
      
      // If email error, go back to step 1 so user can change email
      if (errorMessage.toLowerCase().includes('email') && 
          errorMessage.toLowerCase().includes('already registered')) {
        setCurrentStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Join PakPoints and connect with your community"
    >
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-medium ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            Account Details
          </span>
          <span className={`text-sm font-medium ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            Personal Information
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 2) * 100}%` }}
          ></div>
        </div>
      <div className="text-center text-xs text-gray-500 mt-1">
        Step {currentStep} of 2
      </div>
      </div>

      {/* Registration Error Display */}
      {registrationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Registration Failed</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{registrationError}</p>
              </div>
              {registrationError.toLowerCase().includes('email') && 
               registrationError.toLowerCase().includes('already registered') && (
                <div className="mt-3">
                  <button
                    onClick={() => onNavigate('login')}
                    className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                  >
                    Go to Login instead
                  </button>
                </div>
              )}
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={() => setRegistrationError('')}
                className="text-red-400 hover:text-red-500"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {renderStep()}
    </AuthLayout>
  );
};

export default MultiStepSignup;
